import { useQuery } from "@tanstack/react-query"

import type { ReportResult } from "@ibada/shared-types/reporting"

import { buildReportingScopeDefinition } from "@/features/reports/hooks/use-report-builder"
import { useReportsRouteScope } from "@/features/reports/hooks/use-reports-route-scope"
import { reportsApi } from "@/features/reports/services/reports-api"

/**
 * Runs an ADR-0013 (Reporting Platform Foundation) reporting-engine template and returns its {@link ReportResult}. This is the bridge that
 * lets a Finance Report Catalog entry with a `{ kind: "engine-template" }` data source render through the
 * same shell as the accounting reports. Scope comes from the reporting route scope (workspace/branch).
 */
export function useEngineTemplateResult(
  templateId: string | null,
  inputs: Record<string, unknown> | undefined,
  enabled: boolean,
) {
  const scope = useReportsRouteScope()

  return useQuery({
    queryKey: [
      "finance-reports",
      "engine-template",
      scope?.workspaceId,
      scope?.branchId,
      scope?.scopeKind,
      templateId,
      inputs,
    ],
    enabled: enabled && Boolean(templateId) && Boolean(scope),
    queryFn: async (): Promise<ReportResult> => {
      if (!scope || !templateId) throw new Error("Missing reporting scope or template id")
      return reportsApi.runTemplate({
        templateId,
        scope: buildReportingScopeDefinition(scope),
        inputs,
      })
    },
  })
}
